/**
 * Color Utility Helpers
 * Provides basic hex color validation and transformations
 */

const HEX_COLOR_REGEX = /^#?([0-9a-fA-F]{6})$/;

export function normalizeHexColor(input?: string | null): string | null {
  if (!input) return null;
  const match = input.trim().match(HEX_COLOR_REGEX);
  if (!match) return null;
  const [, hex] = match;
  if (!hex) return null;
  return `#${hex.toLowerCase()}`;
}

interface RgbColor {
  r: number;
  g: number;
  b: number;
}

function hexToRgb(hex: string): RgbColor | null {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return null;

  const value = normalized.replace('#', '');
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);

  return { r, g, b };
}

function rgbToHex({ r, g, b }: RgbColor): string {
  const toHex = (channel: number) => channel.toString(16).padStart(2, '0');

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function mixChannel(channel: number, target: number, amount: number) {
  return Math.round(channel + (target - channel) * amount);
}

function clamp01(value: number) {
  return Math.min(1, Math.max(0, value));
}

function adjustColor(hex: string, target: 0 | 255, amount: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) {
    return '#000000';
  }

  const t = clamp01(amount);
  return rgbToHex({
    r: mixChannel(rgb.r, target, t),
    g: mixChannel(rgb.g, target, t),
    b: mixChannel(rgb.b, target, t),
  });
}

export function lightenHexColor(hex: string, amount: number): string {
  return adjustColor(hex, 255, amount);
}

export function darkenHexColor(hex: string, amount: number): string {
  return adjustColor(hex, 0, amount);
}

// Returns the WCAG AA-safe black or white foreground for a solid accent fill.
export function contrastForeground(hex: string): '#000000' | '#ffffff' {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#ffffff';
  const luminance = [rgb.r, rgb.g, rgb.b]
    .map((channel) => {
      const value = channel / 255;
      return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
    })
    .reduce((sum, value, index) => sum + value * [0.2126, 0.7152, 0.0722][index]!, 0);
  return luminance > 0.179 ? '#000000' : '#ffffff';
}

export function rgbTupleFromHex(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) {
    return '0, 0, 0';
  }

  return `${rgb.r}, ${rgb.g}, ${rgb.b}`;
}
